import { Box, Button, CircularProgress, Container, Paper, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAppDispatch } from "../../store/store";
import { enqueueSnackbar } from "../../store/app-slice/app";
import { EndPoints } from "../../axios/endPoints";
import { axiosInstance } from "../../axios/axiosHandler";
import { BasicResponse } from "../../types/auth.types";
import { handleNotificationVariant } from "../notifications/_common";
import { authHeader } from "../../store/auth-slice/auth-slice";

interface SupportMessageForm {
    subject: string;
    message:string;
} 

const defaultValues: SupportMessageForm = {
    subject: '',
    message: ''
};

interface SupportEmailRequestModel {
    subject: string;
    body: string;
    captchaToken: string;
} 

const schema = (t: Function) =>
    yup.object().shape({
        subject: yup.string().required(t('SUPPORT_SUBJECT_REQUIRED')),
        message: yup.string().required(t('SUPPORT_MESSAGE_REQUIRED')),
    });

export default function Support() {

    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const dispatch = useAppDispatch();

    const { control, handleSubmit, formState: { errors }, reset } = useForm<SupportMessageForm>({
        mode: 'onBlur',
        resolver: yupResolver(schema(t)),
        defaultValues
    });

    const onSubmit = async (data: SupportMessageForm) => {

        if (!executeRecaptcha) {
            dispatch(
                enqueueSnackbar({
                    message: t('BE_USERMGMT_CAPTCHA_NOT_LOADED'),
                    options: { variant: 'error', persist: true },
                })
            );
            return;
        }

        setIsSubmitting(true);

        try {
            const recaptchaToken = await executeRecaptcha('SendEmailToSupport');

            if (!recaptchaToken || recaptchaToken.trim() === "") {
                dispatch(
                    enqueueSnackbar({
                        message: t('BE_USERMGMT_CAPTCHA_BLOCKED'),
                        options: { variant: 'error', persist: true, style: { whiteSpace: "pre-line" } },
                    })
                );
                return;
            }

            const payload: SupportEmailRequestModel = {
                subject: data.subject,
                body: data.message,
                captchaToken: recaptchaToken
            }

            const response = await axiosInstance.post<BasicResponse>(EndPoints.SEND_EMAIL_TO_SUPPORT, payload, authHeader());
            const res = response.data;

            dispatch(
                enqueueSnackbar({
                                message: t(`${res.message}`),
                                options: {
                                    variant: handleNotificationVariant(res.success),
                                    persist: !res.success,
                                },
                            })
                        );
            if(res.success)
                reset();

            }
        catch(e: any){
            const message = e?.response?.data?.message ?? 'BE_GENERIC_ERROR';
            dispatch(
                enqueueSnackbar({
                    message: t(`${message}`),
                    options: { variant: 'error', persist: true },
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 4 } }}>
            <Box sx={{ margin: "0 auto" }}>
                <Paper

                    elevation={3}
                    sx={{ padding: '30px', marginBottom: '40px', borderRadius: '10px', backgroundColor: '#f8f9fa' }}
                >

                    <Typography variant="h5" component="h2" sx={{ marginBottom: '15px', color: '#333' }}>
                        {t('SUPPORT_HEADER_TITLE')}
                    </Typography>

                    <Typography sx={{ fontSize: '16px', lineHeight: '1.6', color: '#555' }} paragraph>
                        {t('SUPPORT_DESCRIPTION')}
                    </Typography>
                    <Box sx={{}}>
                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            <Controller
                                control={control}
                                name="subject"
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        variant="outlined"
                                        label={t('SUPPORT_SUBJECT_LABEL')}
                                        type="text"
                                        error={!!error}
                                        helperText={error ? errors.subject?.message : ''}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="message"
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        variant="outlined"
                                        label={t('SUPPORT_MESSAGE_LABEL')}
                                        placeholder={t('SUPPORT_MESSAGE_PLACEHOLDER')}
                                        multiline
                                        rows={4}
                                        type="text"
                                        error={!!error}
                                        helperText={error ? errors.message?.message : ''}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                    />
                                )}
                            />

                            <Button
                                className="button-color"
                                disabled={isSubmitting}
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {isSubmitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('SUPPORT_SUBMIT_BUTTON')}
                            </Button>
                        </form>

                    </Box>
                </Paper>
            </Box>
        </Container>
    )
}